import { IconLink, IconTrash } from '@tabler/icons-react';
import {
  EmployerSignupStatus,
  EmployerStatus,
  GetEmployerDto,
} from '@zorro/clients';
import { Alert, AlertVariant } from '@zorro/design-system/v2/Alert';
import { Button } from '@zorro/design-system/v2/Button';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { showSuccessNotification } from '@zorro/web/ui-utils';

import { CopyToClipboard } from '../../CopyToClipboard';

const reviewStatusInfoConfig: {
  [key in EmployerStatus]?: {
    getTitle: (employerName: string) => string;
    getSubtitle: (employerName: string) => string;
    variant: AlertVariant;
    shouldShowButtons?: boolean;
  };
} = {
  [EmployerStatus.WAITING_FOR_EMPLOYER]: {
    getTitle: (employerName) =>
      `Waiting for ${employerName} to complete their company profile`,
    getSubtitle: (employerName) =>
      `You've invited ${employerName} to complete their profile. We'll notify you once they're done`,
    variant: 'warning',
    shouldShowButtons: true,
  },
  [EmployerStatus.NEEDS_REVIEW]: {
    getTitle: () => 'Account awaiting review and finalization',
    getSubtitle: (employerName) =>
      `${employerName} has completed their profile. Please review and finalize`,
    variant: 'intro',
  },
  [EmployerStatus.IN_ZORRO_SETUP]: {
    getTitle: () => 'Account in Zorro setup',
    getSubtitle: () =>
      'Zorro team is preparing the account. We will notify you once it is active and ready for employee onboarding',
    variant: 'callout',
  },
};

type Props = {
  employer: GetEmployerDto;
  setIsCancelInviteModalOpen: (isOpen: boolean) => void;
};

export const ReviewStatus = ({
  employer,
  setIsCancelInviteModalOpen,
}: Props) => {
  const { name, signupStatus, invitationLink } = employer;
  const statusInfo = reviewStatusInfoConfig[signupStatus];
  if (!statusInfo) return null;
  const { getTitle, getSubtitle, variant } = statusInfo;

  const shouldShowCopyInvite =
    signupStatus === EmployerSignupStatus.WAITING_FOR_EMPLOYER &&
    Boolean(invitationLink);

  return (
    <Alert variant={variant}>
      <Stack gap="xs">
        <Text fw={600}>{getTitle(name)}</Text>
        <Text fw={400} size="sm">
          {getSubtitle(name)}
        </Text>
        {shouldShowCopyInvite && (
          <Group mt="sm">
            <CopyToClipboard
              text={invitationLink || ''}
              onCopy={() =>
                showSuccessNotification({
                  title: '',
                  message: 'Invitation link copied to clipboard',
                })
              }
            >
              <Button size="md" variant="secondary" leftSection={<IconLink />}>
                Copy invitation link
              </Button>
            </CopyToClipboard>
            <Button
              size="md"
              variant="tertiary"
              leftSection={<IconTrash />}
              onClick={() => setIsCancelInviteModalOpen(true)}
            >
              Cancel invitation
            </Button>
          </Group>
        )}
      </Stack>
    </Alert>
  );
};
